/**
 * AdminReminderSettings - Automated reminder scheduling and history
 * 
 * Features:
 * - Enable/disable automated reminders
 * - Set daily schedule time (PT)
 * - Manual "Run Now" button
 * - View last run status and results
 * - Reminder send history
 */

import React, { useState, useEffect, useCallback } from 'react';
import { reminderSettingsAPI } from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  Bell,
  Play,
  Clock,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Loader2,
  History,
  Settings,
  Mail,
  SkipForward,
  RefreshCcw,
  Calendar,
} from 'lucide-react';

// Generate hour options (0-23)
const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => ({
  value: i.toString(),
  label: `${i.toString().padStart(2, '0')}:00 PT`,
  display: i === 0 ? '12:00 AM' : i < 12 ? `${i}:00 AM` : i === 12 ? '12:00 PM' : `${i - 12}:00 PM`
}));

export default function AdminReminderSettings() {
  const [settings, setSettings] = useState(null);
  const [stats, setStats] = useState({ sent: 0, failed: 0, skipped: 0, total: 0 });
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [showConfirmRun, setShowConfirmRun] = useState(false);
  const [processResult, setProcessResult] = useState(null);
  const [activeTab, setActiveTab] = useState('settings');
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);
  const PAGE_SIZE = 20;

  // Load settings and stats
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [settingsRes, statsRes] = await Promise.all([
        reminderSettingsAPI.getSettings(),
        reminderSettingsAPI.getStats(30)
      ]);
      setSettings(settingsRes);
      setStats(statsRes);
    } catch (error) {
      console.error('Failed to load reminder settings:', error);
      toast.error('Failed to load reminder settings');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load history
  const loadHistory = useCallback(async () => {
    try {
      const res = await reminderSettingsAPI.getHistory({ page: historyPage, page_size: PAGE_SIZE });
      setHistory(res.items || []);
      setHistoryTotal(res.total || 0);
    } catch (error) {
      console.error('Failed to load reminder history:', error);
    }
  }, [historyPage]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (activeTab === 'history') {
      loadHistory();
    }
  }, [activeTab, loadHistory]);

  // Update settings
  const handleUpdateSettings = async (updates) => {
    try {
      setSaving(true);
      const res = await reminderSettingsAPI.updateSettings(updates);
      setSettings(res.settings);
      toast.success('Settings updated successfully');
    } catch (error) {
      console.error('Failed to update settings:', error);
      toast.error('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  // Process reminders manually
  const handleProcessReminders = async () => {
    setShowConfirmRun(false);
    setProcessing(true);
    setProcessResult(null);
    
    try {
      const result = await reminderSettingsAPI.processDue();
      setProcessResult(result);
      
      // Refresh settings to get updated last_run info
      await loadData();
      
      if (result.sent_count > 0) {
        toast.success(`Sent ${result.sent_count} reminder(s)`);
      } else if (result.skipped_count > 0) {
        toast.info(`No reminders sent. ${result.skipped_count} skipped.`);
      } else {
        toast.info('No reminders due today');
      }
      
      // Refresh history if on that tab
      if (activeTab === 'history') {
        await loadHistory();
      }
    } catch (error) {
      console.error('Failed to process reminders:', error);
      toast.error('Failed to process reminders: ' + (error.response?.data?.detail || error.message));
    } finally {
      setProcessing(false);
    }
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: 'America/Los_Angeles'
      });
    } catch {
      return dateStr;
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    switch (status) {
      case 'sent':
        return <Badge className="bg-green-100 text-green-800"><CheckCircle2 className="h-3 w-3 mr-1" /> Sent</Badge>;
      case 'failed':
        return <Badge className="bg-red-100 text-red-800"><XCircle className="h-3 w-3 mr-1" /> Failed</Badge>;
      case 'skipped':
        return <Badge className="bg-gray-100 text-gray-800"><SkipForward className="h-3 w-3 mr-1" /> Skipped</Badge>;
      case 'success':
        return <Badge className="bg-green-100 text-green-800"><CheckCircle2 className="h-3 w-3 mr-1" /> Success</Badge>;
      case 'partial':
        return <Badge className="bg-yellow-100 text-yellow-800"><AlertTriangle className="h-3 w-3 mr-1" /> Partial</Badge>;
      default:
        return <Badge variant="secondary">{status || '-'}</Badge>;
    }
  };

  // Format reminder type for display
  const formatReminderType = (type) => {
    if (!type) return '-';
    
    const mapping = {
      'before_14d': '14 days before due',
      'before_7d': '7 days before due',
      'before_3d': '3 days before due',
      'before_1d': '1 day before due',
      'due_today': 'Due today',
      'after_1d': '1 day overdue',
      'after_3d': '3 days overdue',
      'after_7d': '7 days overdue',
      'after_14d': '14 days overdue',
      'after_21d': '21 days overdue',
      'after_30d': '30 days overdue',
    };
    
    return mapping[type] || type;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reminder-settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Bell className="h-6 w-6" />
            Automated Reminders
          </h1>
          <p className="text-muted-foreground mt-1">
            Configure automated payment reminder emails
          </p>
        </div>
        <Button
          onClick={() => setShowConfirmRun(true)}
          disabled={processing}
          data-testid="run-now-btn"
        >
          {processing ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Processing...
            </>
          ) : (
            <>
              <Play className="h-4 w-4 mr-2" />
              Run Now
            </>
          )}
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="settings" data-testid="settings-tab">
            <Settings className="h-4 w-4 mr-2" />
            Settings
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="history-tab">
            <History className="h-4 w-4 mr-2" />
            History
          </TabsTrigger>
        </TabsList>

        {/* Settings Tab */}
        <TabsContent value="settings" className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Sent (30 days)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">{stats.sent}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Failed (30 days)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-red-600">{stats.failed}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Skipped (30 days)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-gray-600">{stats.skipped}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Total Processed
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.total}</div>
              </CardContent>
            </Card>
          </div>

          {/* Settings Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Schedule Configuration
              </CardTitle>
              <CardDescription>
                Configure when automated reminders are processed
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Enable/Disable Toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="enabled" className="text-base font-medium">
                    Enable Automated Reminders
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    When enabled, reminders will be processed daily at the scheduled time
                  </p>
                </div>
                <Switch
                  id="enabled"
                  checked={settings?.enabled || false}
                  onCheckedChange={(checked) => handleUpdateSettings({ enabled: checked })}
                  disabled={saving}
                  data-testid="enabled-switch"
                />
              </div>

              {/* Schedule Time */}
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-base font-medium">
                    Daily Schedule Time
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Time of day to process reminders (Pacific Time)
                  </p>
                </div>
                <Select
                  value={settings?.scheduled_hour?.toString() || '8'}
                  onValueChange={(value) => handleUpdateSettings({ scheduled_hour: parseInt(value) })}
                  disabled={saving}
                >
                  <SelectTrigger className="w-[180px]" data-testid="schedule-hour-select">
                    <SelectValue placeholder="Select time" />
                  </SelectTrigger>
                  <SelectContent>
                    {HOUR_OPTIONS.map((hour) => (
                      <SelectItem key={hour.value} value={hour.value}>
                        {hour.display} PT
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Next Run Info */}
              {settings?.enabled && settings?.next_run_at && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-blue-800">
                    <Calendar className="h-4 w-4" />
                    <span className="font-medium">Next scheduled run:</span>
                    <span>{formatDate(settings.next_run_at)}</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Last Run Status Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="h-5 w-5" />
                Last Run Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              {settings?.last_run_at ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Run Time</p>
                      <p className="font-medium">{formatDate(settings.last_run_at)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Status</p>
                      <div className="mt-1">{getStatusBadge(settings.last_run_status)}</div>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Sent</p>
                      <p className="font-medium text-green-600">{settings.last_run_sent_count || 0}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Failed</p>
                      <p className="font-medium text-red-600">{settings.last_run_failed_count || 0}</p>
                    </div>
                  </div>
                  {settings.last_run_error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <p className="text-sm text-red-800">
                        <AlertTriangle className="h-4 w-4 inline mr-2" />
                        {settings.last_run_error}
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-muted-foreground">No runs yet. Click "Run Now" to process due reminders.</p>
              )}
            </CardContent>
          </Card>

          {/* Process Result Card (shown after manual run) */}
          {processResult && (
            <Card className="border-2 border-blue-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-blue-800">
                  <CheckCircle2 className="h-5 w-5" />
                  Run Complete
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">{processResult.sent_count}</div>
                    <div className="text-sm text-muted-foreground">Sent</div>
                  </div>
                  <div className="text-center p-4 bg-red-50 rounded-lg">
                    <div className="text-2xl font-bold text-red-600">{processResult.failed_count}</div>
                    <div className="text-sm text-muted-foreground">Failed</div>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <div className="text-2xl font-bold text-gray-600">{processResult.skipped_count}</div>
                    <div className="text-sm text-muted-foreground">Skipped</div>
                  </div>
                </div>
                
                {/* Details table */}
                {processResult.details && processResult.details.length > 0 && (
                  <div className="mt-4">
                    <h4 className="font-medium mb-2">Details:</h4>
                    <div className="max-h-60 overflow-y-auto border rounded">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Invoice</TableHead>
                            <TableHead>Type</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Info</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {processResult.details.map((detail, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="font-mono text-sm">
                                {detail.invoice_number}
                              </TableCell>
                              <TableCell>{formatReminderType(detail.reminder_type)}</TableCell>
                              <TableCell>{getStatusBadge(detail.status)}</TableCell>
                              <TableCell className="text-sm text-muted-foreground">
                                {detail.recipient || detail.reason || detail.error || '-'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Info Box */}
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="pt-6">
              <div className="flex gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="space-y-2 text-sm text-amber-800">
                  <p className="font-medium">How Automated Reminders Work:</p>
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    <li>Only invoices with status <strong>ISSUED</strong> and balance due are eligible</li>
                    <li>Reminder schedule must be configured on each invoice (Order Detail → Payment Reminders)</li>
                    <li>Each reminder type is sent only once per invoice (deduplication)</li>
                    <li>Maximum 1 email per invoice per run (most urgent type takes priority)</li>
                    <li>Failed reminders will retry on the next run (max 2 attempts)</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Reminder History (Last 30 Days)</CardTitle>
                <Button variant="outline" size="sm" onClick={loadHistory}>
                  <RefreshCcw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {history.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">
                  No reminder history yet.
                </p>
              ) : (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Invoice</TableHead>
                        <TableHead>Reminder Type</TableHead>
                        <TableHead>Recipient</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {history.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell className="text-sm">
                            {formatDate(item.created_at)}
                          </TableCell>
                          <TableCell className="font-mono text-sm">
                            {item.invoice_number || item.invoice_id}
                          </TableCell>
                          <TableCell>{formatReminderType(item.reminder_type)}</TableCell>
                          <TableCell className="text-sm">{item.recipient_email}</TableCell>
                          <TableCell>
                            {getStatusBadge(item.status)}
                            {item.error_message && (
                              <p className="text-xs text-red-600 mt-1 max-w-xs truncate" title={item.error_message}>
                                {item.error_message}
                              </p>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  
                  {/* Pagination */}
                  {historyTotal > PAGE_SIZE && (
                    <div className="flex items-center justify-between mt-4">
                      <p className="text-sm text-muted-foreground">
                        Showing {((historyPage - 1) * PAGE_SIZE) + 1} - {Math.min(historyPage * PAGE_SIZE, historyTotal)} of {historyTotal}
                      </p>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setHistoryPage(p => Math.max(1, p - 1))}
                          disabled={historyPage === 1}
                        >
                          Previous
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setHistoryPage(p => p + 1)}
                          disabled={historyPage * PAGE_SIZE >= historyTotal}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Confirm Run Dialog */}
      <AlertDialog open={showConfirmRun} onOpenChange={setShowConfirmRun}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Process Due Reminders?</AlertDialogTitle>
            <AlertDialogDescription>
              This will check all eligible invoices and send payment reminder emails for any reminders due today.
              <br /><br />
              Are you sure you want to continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleProcessReminders} data-testid="confirm-run-btn">
              <Mail className="h-4 w-4 mr-2" />
              Process Reminders
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
